import { useState } from 'react'
import {
  StarterCheckout,
  type StarterCommitment,
} from '@/components/audition/starter-checkout'
import type { AuditionStage } from '@/lib/audition/state-machine'
import type { SubmissionAddress } from '@/lib/audition/submission-address'

/**
 * The consideration lane's "Complete your enrollment" CTA (spec §1.4).
 *
 * This is the landing spot for the link in the CIO unlock email, and it is
 * deliberately a page of ours rather than a Stripe payment link: the guards
 * in `audition.createCheckout` — cooldown and stage — run when the applicant
 * clicks, so someone rejected after the email went out cannot still pay. It
 * also can't go stale the way a 24h Checkout Session would.
 *
 * Persistent by design: it renders on every visit while the stage says
 * payment is open, so an applicant who closes the tab can come back.
 */
export function EnrollmentCta({
  address,
  stage,
}: {
  address: SubmissionAddress
  // The literal union, not `string`: a typo or a renamed stage would
  // otherwise make this comparison silently always-false and delete the
  // consideration lane's only payment CTA with no compile error.
  stage: AuditionStage
}) {
  // The Session and the paid latch live up here, not in the panel. `stage`
  // is the hub's own read of the row, and the webhook flips it to `client`
  // the moment the charge lands — so the next refetch (a tab switch after
  // 30s is enough) would unmount the panel while it is the only thing on
  // the page saying "Payment received". Owning both means a reopened panel
  // reuses its Session and a paid one stays put whatever the stage says.
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [paid, setPaid] = useState(false)

  // Only `payment_unlocked` reaches here in the consideration lane. The fast
  // lane pays from the wizard end (LaneChoice), and every other stage either
  // hasn't been approved yet or has already paid. Guarding here — before
  // StarterCheckout ever mounts — is what keeps the address resolve + DB read +
  // Clerk call inside `listPacks` from firing on every non-payable visit.
  if (stage !== 'payment_unlocked' && !paid) return null

  const commitment: StarterCommitment = {
    clientSecret,
    onClientSecret: setClientSecret,
    paid,
    onPaid: () => setPaid(true),
  }

  return (
    <section className="space-y-3 rounded-md border p-4">
      <h3 className="text-lg font-semibold">You're in — let's get started</h3>
      <p className="text-sm text-muted-foreground">
        We'd love to work with you. This is your first month's payment; it
        starts the program and gets you access.
      </p>
      <StarterCheckout
        address={address}
        submitLabel="Complete enrollment"
        commitment={commitment}
      />
    </section>
  )
}
